import React from 'react';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';

export interface Column<T> {
  id: string;
  header: React.ReactNode;
  className?: string;
  cellClassName?: string;
  /** Label shown in mobile card rows; defaults to header text when string. */
  mobileLabel?: string;
  /** Primary column becomes the card title on mobile. */
  mobilePriority?: 'primary' | 'detail';
  /** Hide from mobile card layout (e.g. checkbox columns). */
  hideOnMobile?: boolean;
  render: (row: T) => React.ReactNode;
}

export interface DataListTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Optional per-row class (e.g. muted tint for compliance state). */
  getRowClassName?: (row: T) => string | undefined;
  loading?: boolean;
  emptyContent: React.ReactNode;
  skeletonColumns?: number;
}

function headerLabel(header: React.ReactNode): string {
  if (typeof header === 'string' || typeof header === 'number') {
    return String(header);
  }
  return '';
}

function mobileColumns<T>(columns: Column<T>[]): Column<T>[] {
  return columns.filter((c) => !c.hideOnMobile);
}

function primaryColumn<T>(columns: Column<T>[]): Column<T> | undefined {
  const visible = mobileColumns(columns);
  return visible.find((c) => c.mobilePriority === 'primary') ?? visible[0];
}

function detailColumns<T>(columns: Column<T>[], primary: Column<T> | undefined): Column<T>[] {
  return mobileColumns(columns).filter((c) => c.id !== primary?.id);
}

function rowInteractionProps<T>(
  row: T,
  onRowClick: ((row: T) => void) | undefined,
  extraClass = ''
): React.HTMLAttributes<HTMLElement> {
  if (!onRowClick) {
    return { className: extraClass };
  }
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onRowClick(row),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick(row);
      }
    },
    className: [
      extraClass,
      'cursor-pointer transition-colors hover:bg-surface-muted/60 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary',
    ]
      .filter(Boolean)
      .join(' '),
  };
}

export function DataListTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  getRowClassName,
  loading,
  emptyContent,
  skeletonColumns,
}: DataListTableProps<T>): React.ReactElement {
  if (loading) {
    return <TableLoadingSkeleton rows={8} columns={skeletonColumns ?? columns.length} />;
  }

  if (rows.length === 0) {
    return <>{emptyContent}</>;
  }

  const primary = primaryColumn(columns);
  const details = detailColumns(columns, primary);

  return (
    <div className="overflow-hidden rounded-xl border border-border shadow-sm">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-muted/80">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  className={`px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-fg-muted sm:px-4 sm:py-3 sm:text-xs ${col.className ?? ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {rows.map((row) => (
              <tr
                key={getRowKey(row)}
                {...rowInteractionProps(row, onRowClick, getRowClassName?.(row) ?? '')}
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className={`px-2 py-2 text-xs text-fg sm:px-4 sm:py-3 sm:text-sm ${col.cellClassName ?? 'whitespace-normal sm:whitespace-nowrap'}`}
                    onClick={(e) => {
                      const t = e.target as HTMLElement;
                      if (t.closest('[data-row-action]')) e.stopPropagation();
                    }}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden divide-y divide-border bg-surface">
        {rows.map((row) => {
          const key = getRowKey(row);
          const rowClass = getRowClassName?.(row) ?? '';
          return (
            <li
              key={key}
              {...rowInteractionProps(row, onRowClick, `block p-4 ${rowClass}`)}
            >
              {primary ? (
                <div
                  className="text-sm font-semibold text-fg"
                  onClick={(e) => {
                    const t = e.target as HTMLElement;
                    if (t.closest('[data-row-action]')) e.stopPropagation();
                  }}
                >
                  {primary.render(row)}
                </div>
              ) : null}
              {details.length > 0 ? (
                <dl className="mt-2 grid grid-cols-[minmax(5rem,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
                  {details.map((col) => (
                    <React.Fragment key={col.id}>
                      <dt className="text-fg-muted text-xs font-medium uppercase tracking-wide">
                        {col.mobileLabel ?? (headerLabel(col.header) || col.id)}
                      </dt>
                      <dd
                        className="text-fg text-sm min-w-0"
                        onClick={(e) => {
                          const t = e.target as HTMLElement;
                          if (t.closest('[data-row-action]')) e.stopPropagation();
                        }}
                      >
                        {col.render(row)}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Spec alias — same component as DataListTable with responsive mobile cards. */
export const ResponsiveTable = DataListTable;

export default DataListTable;
